/**
 * Where the period chart's marks go. Each axis is ONE scale (`plotScale`): its ticks and every mark
 * drawn against it come from the same function, so a bar of 2,000 stands on the gridline that says
 * 2,000. Series that share the first series' unit (the same `format`) share the left axis; a series in
 * another unit gets the right axis and its own scale. Pure, so it is tested without a DOM.
 */
import { plotScale, type PlotScale } from '@paige/ui';
import type { Period } from './period';

export type ChartTone = 'accent' | 'success' | 'warning' | 'danger' | 'muted';

export interface ChartSeries {
  key: string;
  label: string;
  values: readonly number[];
  kind: 'bars' | 'line';
  /** Formats a value of this series. Series in the same unit pass the SAME function: it is how the chart knows they share an axis. */
  format: (n: number) => string;
  tone?: ChartTone | undefined;
}

export interface Plot {
  width: number;
  height: number;
  pad: { top: number; right: number; bottom: number; left: number };
  innerW: number;
  innerH: number;
  /** The width of one bucket. */
  step: number;
  count: number;
  left: PlotScale;
  /** The second unit's scale, when a series has one. */
  right: PlotScale | null;
  /** Which series is drawn against the right axis. */
  onRight: readonly boolean[];
  narrow: boolean;
}

export interface PlotInput {
  width: number;
  height: number;
  count: number;
  series: readonly ChartSeries[];
  previous?: readonly number[] | undefined;
}

/** True for each series in another unit than the first: those go on the right axis. */
export function rightSeries(series: readonly ChartSeries[]): boolean[] {
  const unit = series[0]?.format;
  return series.map((s) => s.format !== unit);
}

/** The plot for these series at this size: the paddings, the bucket width and the one or two scales. */
export function plotOf(input: PlotInput): Plot {
  const { width, height, count, series } = input;
  const onRight = rightSeries(series);
  const narrow = width < 480;
  const side = narrow ? 38 : 52;
  const hasRight = onRight.some(Boolean);
  // Two axes are each named above their values, which takes a line.
  const pad = { top: hasRight ? 24 : 10, right: hasRight ? side : 10, bottom: 24, left: side };
  const innerH = Math.max(1, height - pad.top - pad.bottom);
  const innerW = Math.max(1, width - pad.left - pad.right);
  const of = (right: boolean) => series.filter((_, i) => onRight[i] === right);
  const leftValues = [...of(false).flatMap((s) => [...s.values]), ...(input.previous ?? [])];
  const rightValues = of(true).flatMap((s) => [...s.values]);
  const tickCount = narrow ? 3 : 4;
  return {
    width,
    height,
    pad,
    innerW,
    innerH,
    step: innerW / Math.max(1, count),
    count,
    left: plotScale(leftValues, innerH, pad.top, tickCount),
    right: hasRight ? plotScale(rightValues, innerH, pad.top, tickCount) : null,
    onRight,
    narrow,
  };
}

/** The scale series `index` is drawn against. */
export function scaleFor(plot: Plot, index: number): PlotScale {
  return plot.onRight[index] && plot.right ? plot.right : plot.left;
}

/** The middle of bucket `i`, in the plot's pixels. */
export const midOf = (plot: Plot, i: number): number => plot.pad.left + (i + 0.5) * plot.step;

/** The plot's baseline (zero on every axis). */
export const baseOf = (plot: Plot): number => plot.pad.top + plot.innerH;

/** The bucket under an x in the plot's pixels, clamped to the ends. */
export function indexAt(plot: Plot, x: number): number {
  return Math.min(plot.count - 1, Math.max(0, Math.floor((x - plot.pad.left) / plot.step)));
}

export interface BarBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Bar `slot` of `slots` side by side in bucket `i`, from the baseline up to its value on `scale`. */
export function barBox(
  plot: Plot,
  scale: PlotScale,
  at: { i: number; slot: number; slots: number },
  value: number,
): BarBox {
  const band = Math.min(48 * at.slots, Math.max(1, plot.step * (plot.step > 8 ? 0.72 : 1)));
  const width = band / at.slots;
  const y = scale.y(Math.max(0, value));
  return {
    x: midOf(plot, at.i) - band / 2 + at.slot * width,
    y,
    width: Math.max(1, width - (width > 6 ? 1 : 0)),
    height: Math.max(0, baseOf(plot) - y),
  };
}

/** A line through the bucket middles at the values' heights on `scale`. */
export function linePoints(plot: Plot, scale: PlotScale, values: readonly number[]): string {
  return values
    .slice(0, plot.count)
    .map((v, i) => `${midOf(plot, i).toFixed(1)},${scale.y(Math.max(0, v)).toFixed(1)}`)
    .join(' ');
}

/** Label every this many buckets, so each label has about 56 pixels; the last bucket is always labelled. */
export function labelEvery(plot: Plot): number {
  return Math.max(1, Math.ceil(56 / Math.max(1, plot.step)));
}

/** Which buckets get a label under them: every nth counted back from the last. */
export function labelled(plot: Plot): number[] {
  const every = labelEvery(plot);
  const out: number[] = [];
  for (let i = plot.count - 1; i >= 0; i -= every) out.unshift(i);
  return out;
}

const stamp = (day: string): number => Date.parse(`${day}T00:00:00Z`);

/** A bucket under the axis: "Oct 1" for a day or a week (its first day), "Oct" for a month. */
export function bucketWord(bucket: Period, locale?: string): string {
  const month = stamp(bucket.until) - stamp(bucket.from) >= 27 * 86_400_000;
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    timeZone: 'UTC',
    ...(month ? {} : { day: 'numeric' }),
  }).format(stamp(bucket.from));
}

/** Nothing happened: every value of every series is zero (or there is no bucket at all). */
export function nothingToDraw(series: readonly ChartSeries[], count: number): boolean {
  return count === 0 || series.every((s) => s.values.every((v) => v === 0));
}
